/// *** System import ***///
import { Injectable, Inject } from '@angular/core';
import { Events, ToastController } from 'ionic-angular';
import { hubConnection } from 'signalr-no-jquery';
import { RESOURCE_CACHE_PROVIDER } from '@angular/platform-browser-dynamic';
/// *** Custom import ***///
import { Globals } from '../../app/globals';
import { EnvVariables } from '../../app/environment-variables/environment-variables.token';

@Injectable()
export class InstrumentHubProvider {
  constructor(
    // *** System
    private events: Events,
    private toastCtrl: ToastController,
    // *** Custom
    private globals: Globals,
    @Inject(EnvVariables) private envVariables) {
  }

  // *** Hub subscription
  public subscribe(iSINCode: string) {
    return new Promise((resolve, reject) => {
      try {
        // Init
        let connection = hubConnection(this.envVariables.serverPushEndpoint);
        connection.logging = false;
        let proxy = connection.createHubProxy('webmarketInstrument');
        // Subscriptions
        proxy.on('updateTheoreticalOpeningPrice', this._onUpdateTheoreticalOpeningPrice(this));
        proxy.on('updateStaticThresholds', this._onUpdateStaticThresholds(this));
        proxy.on('updateOpeningSummary', this._onUpdateOpeningSummary(this));
        proxy.on('updatePrice', this._onUpdatePrice(this));
        proxy.on('updateInstrumentTrade', this._onUpdateInstrumentTrade(this));
        proxy.on('updateInstrumentState', this._onUpdateInstrumentState(this));
        proxy.on('updateBestLimit', this._onUpdateBestLimit(this));
        proxy.on('updateMarketDepth', this._onUpdateMarketDepth(this));
        // Start
        connection.start()
          .done(function () {
            proxy.invoke('subscribe', iSINCode);
          })
          .fail(function () {
            connection.stop();
            setTimeout((provider = this) => {
              this.events.publish('InstrumentHubProvider.reconnect', iSINCode);
            }, 5000);
          });
        // Keep alive
        connection.disconnected(function () {
        });
        // On try reconnect
        this.events.subscribe('InstrumentHubProvider.reconnect',
          (iSINCode) => {
            connection.stop();
            setTimeout((provider = this) => {
              provider.subscribe(iSINCode);
            }, 5000);
          });
        // Abord connection
        this.events.subscribe('InstrumentHubProvider.abord', () => { connection.stop(); });
        // On session user logout
        this.events.subscribe('SessionProvider.logout', () => { connection.stop(); });
        resolve();
      }
      catch (error) {
        this.globals.showCommunicationError(this.toastCtrl, error);
        reject();
      }
    });
  }

  // *** 'updateTheoreticalOpeningPrice' handler
  private _onUpdateTheoreticalOpeningPrice(provider: InstrumentHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('InstrumentHubProvider.updateTheoreticalOpeningPrice', instrument);
    }
  }

  // *** 'updateStaticThresholds' handler
  private _onUpdateStaticThresholds(provider: InstrumentHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('InstrumentHubProvider.updateStaticThresholds', instrument);
    }
  }

  // *** 'updateOpeningSummary' handler
  private _onUpdateOpeningSummary(provider: InstrumentHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('InstrumentHubProvider.updateOpeningSummary', instrument);
    }
  }

  // *** 'updatePrice' handler
  private _onUpdatePrice(provider: InstrumentHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('InstrumentHubProvider.updatePrice', instrument);
    }
  }

  // *** 'updateInstrumentTrade' handler
  private _onUpdateInstrumentTrade(provider: InstrumentHubProvider): { (instrumentData: any, tradeData: any): void } {
    return function (instrumentData: any, tradeData: any) {
      let instrument = JSON.parse(instrumentData);
      let trade = JSON.parse(tradeData);
      provider.events.publish('InstrumentHubProvider.updateInstrumentTrade', instrument, trade);
    }
  }

  // *** 'updateInstrumentState' handler
  private _onUpdateInstrumentState(provider: InstrumentHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('InstrumentHubProvider.updateInstrumentState', instrument);
    }
  }

  // *** 'updateBestLimit' handler
  private _onUpdateBestLimit(provider: InstrumentHubProvider): { (bestLimitsData: any): void } {
    return function (bestLimitsData: any) {
      let bestLimits = JSON.parse(bestLimitsData);
      provider.events.publish('InstrumentHubProvider.updateBestLimit', bestLimits);
    }
  }

  // *** 'updateMarketDepth' handler
  private _onUpdateMarketDepth(provider: InstrumentHubProvider): { (marketDepthData: any): void } {
    return function (marketDepthData: any) {
      let marketDepth = JSON.parse(marketDepthData);
      provider.events.publish('InstrumentHubProvider.updateMarketDepth', marketDepth.Root);
    }
  }
}


// WEBPACK FOOTER //
// ./src/providers/serverHubs/instrument.ts